import * as React from "react"

import * as AccordionPrimitives from "@radix-ui/react-accordion"
import { cva, type VariantProps } from "class-variance-authority"
import { ChevronDown } from "lucide-react"

import { cn } from "../../cn"

/* ── CVA variants ─────────────────────────────────────────── */

const accordionItemVariants = cva(["border-b border-coval-border"])

const accordionTriggerVariants = cva([
  "flex flex-1 items-center justify-between py-3 text-sm font-medium cursor-pointer",
  "text-coval-text",
  "transition-colors duration-150",
  "not-disabled:hover:text-coval-text-muted",
  "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-coval-border-active focus-visible:ring-offset-1",
  "disabled:cursor-default disabled:opacity-50",
  "[&[data-state=open]>svg]:rotate-180",
])

const accordionContentVariants = cva([
  "overflow-hidden text-sm text-coval-text",
  "data-[state=open]:animate-accordion-down",
  "data-[state=closed]:animate-accordion-up",
])

/* ── Accordion (root) ────────────────────────────────────── */

const Accordion = AccordionPrimitives.Root

/* ── AccordionItem ───────────────────────────────────────── */

export interface AccordionItemProps
  extends
    React.ComponentPropsWithoutRef<typeof AccordionPrimitives.Item>,
    VariantProps<typeof accordionItemVariants> {}

const AccordionItem = React.forwardRef<
  React.ComponentRef<typeof AccordionPrimitives.Item>,
  AccordionItemProps
>(({ className, ...props }, ref) => (
  <AccordionPrimitives.Item
    ref={ref}
    className={cn(accordionItemVariants(), className)}
    {...props}
  />
))

AccordionItem.displayName = "AccordionItem"

/* ── AccordionTrigger ────────────────────────────────────── */

export interface AccordionTriggerProps
  extends
    React.ComponentPropsWithoutRef<typeof AccordionPrimitives.Trigger>,
    VariantProps<typeof accordionTriggerVariants> {
  // Suppress the built-in chevron when the caller renders its own indicator.
  hideChevron?: boolean
  // Class applied to the wrapping header (e.g. for sticky positioning).
  headerClassName?: string
}

const AccordionTrigger = React.forwardRef<
  React.ComponentRef<typeof AccordionPrimitives.Trigger>,
  AccordionTriggerProps
>(
  (
    { className, children, hideChevron = false, headerClassName, ...props },
    ref,
  ) => (
    <AccordionPrimitives.Header className={cn("flex", headerClassName)}>
      <AccordionPrimitives.Trigger
        ref={ref}
        className={cn(accordionTriggerVariants(), className)}
        {...props}
      >
        {children}
        {!hideChevron && (
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-coval-text-muted transition-transform duration-200" />
        )}
      </AccordionPrimitives.Trigger>
    </AccordionPrimitives.Header>
  ),
)

AccordionTrigger.displayName = "AccordionTrigger"

/* ── AccordionContent ────────────────────────────────────── */

export interface AccordionContentProps
  extends
    React.ComponentPropsWithoutRef<typeof AccordionPrimitives.Content>,
    VariantProps<typeof accordionContentVariants> {}

const AccordionContent = React.forwardRef<
  React.ComponentRef<typeof AccordionPrimitives.Content>,
  AccordionContentProps
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitives.Content
    ref={ref}
    className={cn(accordionContentVariants(), className)}
    {...props}
  >
    <div className="pb-3">{children}</div>
  </AccordionPrimitives.Content>
))

AccordionContent.displayName = "AccordionContent"

export {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
  accordionItemVariants,
  accordionTriggerVariants,
  accordionContentVariants,
}
